import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import './Dashboard.css';
import ApiConnectionTest from '../components/ApiConnectionTest';

const Dashboard = () => {
  const { token } = useAuth();
  const [activeTradableCount, setActiveTradableCount] = useState(0);
  const [loadingStats, setLoadingStats] = useState(true);
  const [gains, setGains] = useState(0);
  const [loadingGains, setLoadingGains] = useState(true);
  const [upToDateHistoryCount, setUpToDateHistoryCount] = useState(0);
  const [loadingHistoryStats, setLoadingHistoryStats] = useState(true);

  useEffect(() => {
    const loadStats = async () => {
      try {
        const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
        const [statsResponse, gainsResponse] = await Promise.all([
          fetch('/api/alpaca/assets/stats', { headers }),
          fetch('/api/alpaca/gains', { headers })
        ]);
        
        if (statsResponse.ok) {
          const data = await statsResponse.json();
          if (data.success) {
            setActiveTradableCount(data.active_tradable_count || 0);
            setUpToDateHistoryCount(data.up_to_date_history_count || 0);
          }
        }

        if (gainsResponse.ok) {
          const gainsData = await gainsResponse.json();
          if (gainsData.success) {
            setGains(gainsData.total_gains || 0);
          }
        }
      } catch (e) {
        console.error('Erreur lors du chargement des statistiques:', e);
      } finally {
        setLoadingStats(false);
        setLoadingGains(false);
        setLoadingHistoryStats(false);
      }
    };

    if (token) {
      loadStats();
    }
  }, [token]);

  const stats = [
    {
      title: 'Actions actives et tradables',
      value: loadingStats ? '...' : activeTradableCount.toLocaleString('fr-FR'),
      change: '',
      changeType: '',
      icon: '📈'
    },
    {
      title: 'Gains Alpaca',
      value: loadingGains ? '...' : `${gains >= 0 ? '+' : ''}${gains.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} $`,
      change: '',
      changeType: gains >= 0 ? 'positive' : 'negative',
      icon: '💰'
    },
    {
      title: 'Assets à jour historiquement',
      value: loadingHistoryStats ? '...' : upToDateHistoryCount.toLocaleString('fr-FR'),
      change: '',
      changeType: '',
      icon: '📊'
    }
  ];

  const recentActivities = [
    { id: 1, user: 'Jean Dupont', action: 'a créé un nouveau compte', time: '2 min', avatar: '👤' },
    { id: 2, user: 'Marie Martin', action: 'a passé une commande', time: '5 min', avatar: '👩' },
    { id: 3, user: 'Pierre Durand', action: 'a mis à jour son profil', time: '10 min', avatar: '👨' },
    { id: 4, user: 'Sophie Bernard', action: 'a annulé une commande', time: '15 min', avatar: '👩' },
    { id: 5, user: 'Lucas Petit', action: 'a ajouté un produit', time: '20 min', avatar: '👨' }
  ];

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <h1 className="dashboard-title">📊 Tableau de bord</h1>
        <p className="dashboard-subtitle">Vue d'ensemble de votre activité</p>
      </div>

      {/* Statistiques */}
      <div className="stats-grid">
        {stats.map((stat, index) => (
          <div key={index} className="stat-card">
            <div className="stat-icon">{stat.icon}</div>
            <div className="stat-content">
              <div className="stat-value">{stat.value}</div>
              <div className="stat-title">{stat.title}</div>
              {stat.change && (
                <div className={`stat-change ${stat.changeType}`}>
                  {stat.change} vs mois dernier
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Test de connexion API */}
      <ApiConnectionTest />

      {/* Contenu principal */}
      <div className="dashboard-content">
        {/* Activités récentes */}
        <div className="dashboard-section">
          <div className="section-header">
            <h2 className="section-title">Activités récentes</h2>
            <button className="view-all-btn">Voir tout</button>
          </div>
          <div className="activities-list">
            {recentActivities.map((activity) => (
              <div key={activity.id} className="activity-item">
                <div className="activity-avatar">{activity.avatar}</div>
                <div className="activity-content">
                  <div className="activity-text">
                    <strong>{activity.user}</strong> {activity.action}
                  </div>
                  <div className="activity-time">{activity.time}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Graphique de performance */}
        <div className="dashboard-section">
          <div className="section-header">
            <h2 className="section-title">Performance</h2>
            <div className="chart-controls">
              <button className="chart-btn active">7j</button>
              <button className="chart-btn">30j</button>
              <button className="chart-btn">90j</button>
            </div>
          </div>
          <div className="chart-placeholder">
            <div className="chart-mock">
              <div className="chart-bars">
                {[65, 80, 45, 90, 70, 85, 95].map((height, index) => (
                  <div 
                    key={index} 
                    className="chart-bar" 
                    style={{ height: `${height}%` }}
                  ></div>
                ))}
              </div>
              <div className="chart-labels">
                <span>Lun</span>
                <span>Mar</span>
                <span>Mer</span>
                <span>Jeu</span>
                <span>Ven</span>
                <span>Sam</span>
                <span>Dim</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
